/** Memory and workload: host and service memory, the pressure ladder, read-only workload control (U081-U083). */
import { decodeMemoryRow, type MemoryViewRow } from "../../contracts/ops.ts";
import { badge, definitions, metric } from "../../app/components.ts";
import { h, type Child } from "../../app/dom.ts";
import { PRESSURE_STATES } from "../../contracts/ops.ts";
import { formatBasisPoints, formatDurationUs } from "../../app/format.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { byKindOf, formatMiB, mountOps, panel, simpleTable } from "../ops-common.ts";

type Pressure = Extract<MemoryViewRow, { kind: "pressure" }>;

function ladder(row: Pressure): HTMLElement {
  return h(
    "ol",
    { class: "ladder", attrs: { "aria-label": "Memory-pressure ladder" } },
    ...PRESSURE_STATES.map((state, step) =>
      h(
        "li",
        step === row.ladderStep
          ? { attrs: { "aria-current": "step", "data-ladder": state } }
          : { attrs: { "data-ladder": state } },
        state,
        step === row.ladderStep ? " (current)" : "",
      ),
    ),
  );
}

function pressurePanel(row: Pressure): HTMLElement {
  return panel(
    "Memory pressure",
    "memory-pressure",
    ladder(row),
    definitions([
      [
        "State",
        badge(row.state, row.state === "NORMAL" ? "ok" : row.state === "CRITICAL" ? "bad" : "warn"),
      ],
      ["OOM events", String(row.oomEvents)],
      ["Swap in use", formatMiB(row.swapUsedBytes)],
      ["Pressure stall", formatBasisPoints(row.stallBp)],
      ["Last restart cause", metric(row.lastRestartCause)],
      [
        "Controller recovery",
        badge(row.controllerRecovery, row.controllerRecovery === "INTACT" ? "ok" : "warn"),
      ],
      ["Next ladder action", row.nextAction],
    ]),
  );
}

/** Draw memory, the ladder and the workload. */
export function drawMemory(rows: readonly MemoryViewRow[]): readonly Child[] {
  return [
    panel(
      "Host and service memory (MiB)",
      "memory-table",
      simpleTable(
        "Memory",
        ["Scope", "Available", "In use", "Reserved", "Hard limit", "High-water mark"],
        byKindOf(rows, "memory").map((row) => [
          row.scope,
          metric(row.availableBytes === null ? null : formatMiB(row.availableBytes)),
          formatMiB(row.usedBytes),
          formatMiB(row.reservationBytes),
          formatMiB(row.limitBytes),
          formatMiB(row.highWaterBytes),
        ]),
      ),
    ),
    ...byKindOf(rows, "pressure").map(pressurePanel),
    ...byKindOf(rows, "workload").map((row) =>
      panel(
        "Workload (read-only)",
        "memory-workload",
        definitions([
          [
            "Worker concurrency",
            `${String(row.workerConcurrency)} of ${String(row.concurrencyCap)}`,
          ],
          ["Queued", formatMiB(row.queuedBytes)],
          ["In flight", formatMiB(row.inflightBytes)],
          [
            "Oldest message",
            metric(
              row.oldestMessageAgeUs === null ? null : formatDurationUs(row.oldestMessageAgeUs),
            ),
          ],
          ["Cache occupancy", formatBasisPoints(row.cacheOccupancyBp)],
          [
            "Database connections",
            `${String(row.dbConnectionsUsed)} of ${String(row.dbConnectionsLimit)}`,
          ],
        ]),
        h(
          "p",
          { class: "note" },
          "Pausing research or exports, or reducing optional concurrency, are guarded requests made elsewhere; nothing on this page restarts or stops the live controller to free memory.",
        ),
      ),
    ),
  ];
}

/** Mount the memory view. */
export function mountMemory(ctx: ViewContext): ViewHandle {
  return mountOps(ctx, {
    id: "memory",
    title: "Memory and workload",
    projection: "memory",
    row: decodeMemoryRow,
    draw: (rows) => drawMemory(rows),
  });
}
